import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { APP_CONFIG } from "../config/app.config";

const SignIn: React.FC = () => {
    const location = useLocation();
    
    // Get email from navigation state if passed from SignUp
    const emailFromState = location.state?.email || "";
    
    const [formData, setFormData] = useState({
        email: emailFromState,
        password: "",
    });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showResendVerification, setShowResendVerification] = useState(false);
    const [verificationEmailSent, setVerificationEmailSent] = useState(false);

    const { signIn, signInWithGoogle, resendVerificationEmail, refreshAuthState, firebaseUser, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    // Get the return URL from navigation state
    const from = location.state?.from?.pathname || "/profile";
    
    // Check if user just verified their email
    const urlParams = new URLSearchParams(location.search);
    const emailVerified = urlParams.get('emailVerified') === 'true';

    // Redirect if user is already authenticated
    useEffect(() => {
        if (!authLoading && firebaseUser) {
            // If email verification is required and user is not verified, don't redirect
            if (APP_CONFIG.REQUIRE_EMAIL_VERIFICATION && !firebaseUser.emailVerified) {
                return;
            }
            // User is signed in and verified (or verification not required), redirect
            navigate(from, { replace: true });
        }
    }, [firebaseUser, authLoading, navigate, from]);

    // Refresh auth state on mount to ensure we have latest verification status
    useEffect(() => {
        refreshAuthState();
    }, [refreshAuthState]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        setShowResendVerification(false);
        setVerificationEmailSent(false);

        try {
            await signIn(formData.email, formData.password);
            // Add a small delay to ensure auth state is updated before navigation
            setTimeout(() => {
                navigate(from, { replace: true });
            }, 200);
        } catch (error: any) {
            const errorMessage = error.message || "Failed to sign in";
            setError(errorMessage);
            
            // Check if the error is about email verification
            if (errorMessage.includes("verify your email") && APP_CONFIG.REQUIRE_EMAIL_VERIFICATION) {
                setShowResendVerification(true);
            }
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setError("");
        setLoading(true);

        try {
            await signInWithGoogle();
            navigate(from, { replace: true });
        } catch (error: any) {
            setError(error.message || "Failed to sign in with Google");
        } finally {
            setLoading(false);
        }
    };

    const handleResendVerification = async () => {
        // Validate that email is provided
        if (!formData.email) {
            setError("Please enter your email to resend verification");
            return;
        }
        
        setError("");
        setLoading(true);
        
        try {
            await resendVerificationEmail(formData.email);
            setVerificationEmailSent(true);
            setError("");
        } catch (error: any) {
            setError(error.message || "Failed to resend verification email");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Sign in to your account
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        Or{" "}
                        <Link
                            to="/signup"
                            className="font-medium text-primary-600 hover:text-primary-500"
                        >
                            create a new account
                        </Link>
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    {emailVerified && (
                        <div className="rounded-md bg-green-50 p-4">
                            <div className="text-sm text-green-700">
                                <p className="font-medium">Email verified successfully!</p>
                                <p className="mt-1">You can now sign in to your account.</p>
                            </div>
                        </div>
                    )}

                    {showResendVerification && !verificationEmailSent && (
                        <div className="rounded-md bg-yellow-50 p-4">
                            <div className="text-sm">
                                <p className="text-yellow-700">
                                    Your email address hasn't been verified yet.
                                </p>
                                <p className="text-yellow-600 text-xs mt-1">
                                    Make sure you've entered your email below.
                                </p>
                                <button
                                    type="button"
                                    onClick={handleResendVerification}
                                    disabled={loading}
                                    className="mt-2 text-yellow-700 underline font-medium hover:text-yellow-600"
                                >
                                    Resend verification email
                                </button>
                            </div>
                        </div>
                    )}

                    {verificationEmailSent && (
                        <div className="rounded-md bg-green-50 p-4">
                            <div className="text-sm text-green-700">
                                <p className="font-medium">Verification email sent!</p>
                                <p className="mt-1">
                                    Please check your inbox for {formData.email} and click the verification link.
                                </p>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="rounded-md bg-red-50 p-4">
                            <div className="text-sm text-red-700">{error}</div>
                        </div>
                    )}

                    <div className="space-y-4">
                        <div>
                            <label htmlFor="email" className="sr-only">
                                Email address
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                                placeholder="Email address"
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="sr-only">
                                Password
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={formData.password}
                                onChange={handleChange}
                                className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                                placeholder="Password"
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="text-sm">
                            <Link
                                to="/forgot-password"
                                className="font-medium text-primary-600 hover:text-primary-500"
                            >
                                Forgot your password?
                            </Link>
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <input
                                id="remember-me"
                                name="remember-me"
                                type="checkbox"
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <label
                                htmlFor="remember-me"
                                className="ml-2 block text-sm text-gray-900"
                            >
                                Remember me
                            </label>
                        </div>

                        <div className="text-sm">
                            <Link
                                to="/forgot-password"
                                className="font-medium text-primary-600 hover:text-primary-500"
                            >
                                Forgot your password?
                            </Link>
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? "Signing in..." : "Sign in"}
                        </button>
                    </div>

                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-gray-50 text-gray-500">
                                    Or continue with
                                </span>
                            </div>
                        </div>

                        <div className="mt-6">
                            <button
                                type="button"
                                onClick={handleGoogleSignIn}
                                disabled={loading}
                                className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <path
                                        fill="currentColor"
                                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                    />
                                </svg>
                                <span className="ml-2">
                                    Sign in with Google
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="text-xs text-gray-500 text-center mt-6">
                        By signing in, you agree to our{" "}
                        <a
                            href="https://www.passio.ai/passio-ai-tools-license-terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-500 underline"
                        >
                            Terms & Conditions
                        </a>{" "}
                        and{" "}
                        <a
                            href="https://www.passio.ai/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-500 underline"
                        >
                            Privacy Policy
                        </a>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SignIn;
